import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import {
  getDepositFundsModalState,
  setPaymentMethodType
} from "state/slices/deposit-funds-modal-v2-slice.ts";
import { getAppConfig } from "state/slices/app-config-slice.ts";
import { checkIfApplePayIsAvailable } from "services/apple-pay-service.ts";
import OrSeparator from "../shared/or-separator.tsx";
import PaymentMethodTabs from "./payment-method-tabs.tsx";
import DepositFundsModalCardPaymentBlock from "./deposit-funds-modal-card-payment-block.tsx";
import DepositFundsModalAchPaymentBlock from "./deposit-funds-modal-ach-payment-block.tsx";
import ApplePayButton, { PaymentStatus } from "components/payment-provider/apple-pay/apple-pay-button.tsx";
import GooglePayButton from "components/payment-provider/google-pay/google-pay-button.tsx";

interface DepositFundsModalPaymentMethodsBlockProps {
  onApplePayPayment: (applePayToken: string) => Promise<PaymentStatus>;
  onGooglePayPayment: (googlePayToken: string) => Promise<PaymentStatus>;
}

const STATEMENT_NOTICE = "Transactions will appear on your statement as 'Sparket'.";
const CARD_AUTHORIZATION_NOTICE =
  `By clicking Deposit you authorize the account selected below to be debited one time today by the listed amount. ${STATEMENT_NOTICE}`;
const ACH_AUTHORIZATION_NOTICE =
  `By clicking Deposit you authorize the account selected below to be debited one time today by the listed amount via bank transfer (ACH). ${STATEMENT_NOTICE}`;

const DepositFundsModalPaymentMethodsBlock = ({ onApplePayPayment, onGooglePayPayment }: DepositFundsModalPaymentMethodsBlockProps) => {
  const dispatch = useAppDispatch();
  const modalState = useAppSelector(getDepositFundsModalState);
  const appConfig = useAppSelector(getAppConfig);

  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayVisible, setIsGooglePayVisible] = useState(false);

  useEffect(() => {
    try {
      const applePayIsAvailable = checkIfApplePayIsAvailable();
      setIsApplePayAvailable(applePayIsAvailable);
    } catch (error) {
      console.error("Error checking Apple Pay availability: ", error);
      setIsApplePayAvailable(false);
    }
  }, []);

  const handleTabChange = (tab: 'CARD' | 'ACH') => {
    dispatch(setPaymentMethodType(tab));
  };

  const showDigitalWalletSeparator = isApplePayAvailable || isGooglePayVisible;

  return (
    <div style={{ padding: "8px 4px" }}>
      {/* Apple Pay Button */}
      {isApplePayAvailable && (
        <div className="mb-3">
          <ApplePayButton
            amount={modalState.selectedAmount}
            onPaymentAuthorized={onApplePayPayment}
          />
        </div>
      )}

      {/* Google Pay Button - component handles its own visibility via isReadyToPay() */}
      {appConfig.showGooglePayments && (
        <div className="mb-3">
          <GooglePayButton
            amount={modalState.selectedAmount}
            onPaymentAuthorized={onGooglePayPayment}
            onVisibilityChange={setIsGooglePayVisible}
          />
        </div>
      )}

      {showDigitalWalletSeparator && <OrSeparator />}

      {/* Payment Method Tabs */}
      <PaymentMethodTabs
        activeTab={modalState.paymentMethodType}
        onTabChange={handleTabChange}
      />

      {/* Debit authorization + statement notice (wording differs for bank transfer vs card) */}
      <div style={{ 
        marginTop: '12px', 
        fontSize: '14px', 
        color: 'rgba(255, 255, 255, 0.7)',
        textAlign: 'center'
      }}>
        {modalState.paymentMethodType === 'ACH' ? ACH_AUTHORIZATION_NOTICE : CARD_AUTHORIZATION_NOTICE}
      </div>

      {/* Payment Method Details */}
      <div style={{
        marginTop: '20px',
        padding: '20px 0 0',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        {modalState.paymentMethodType === 'CARD' ? (
          <DepositFundsModalCardPaymentBlock />
        ) : (
          <DepositFundsModalAchPaymentBlock />
        )}
      </div>
    </div>
  );
};

export default DepositFundsModalPaymentMethodsBlock; 